import React, { useState, useEffect, useRef } from 'react';
import { collection, query, where, getDocs } from 'firebase/firestore';
import { db } from '../services/firebase';
import Layout from '../component/Layout';

const PaymentsPaid = () => {
  const [orders, setOrders] = useState([]);
  const operatorId = localStorage.getItem("operatorId");
  const audioContextRef = useRef(null);
  const beepIntervalRef = useRef(null);
  const hasAttemptedInit = useRef(false);

    
  // Try to initialize audio immediately (may fail silently in some browsers)
//   const initAudio = () => {
//     if (audioContextRef.current || hasAttemptedInit.current) return;
//     hasAttemptedInit.current = true;
//     try {
//       const ctx = new (window.AudioContext || window.webkitAudioContext)();
//       audioContextRef.current = ctx;
//     } catch (e) {
//       console.warn("Audio init failed (will retry on click):", e);
//     }
//   };

  // Fallback: init on click if not already done
//   useEffect(() => {
//     const handleClick = () => {
//       initAudio();
//       document.body.removeEventListener('click', handleClick);
//       document.body.removeEventListener('touchstart', handleClick);
//     };
//     document.body.addEventListener('click', handleClick);
//     document.body.addEventListener('touchstart', handleClick);
//     return () => {
//       document.body.removeEventListener('click', handleClick);
//       document.body.removeEventListener('touchstart', handleClick);
//     };
//   }, []);

  // Try init on mount (for environments that allow it)
//   useEffect(() => {
//     initAudio();
//   }, []);

  const playBeep = () => {
    // If context not ready, try to resume (in case it's suspended)
    if (audioContextRef.current?.state === 'suspended') {
      audioContextRef.current.resume().catch(console.warn);
    }

    if (!audioContextRef.current) return;

    try {
      const ctx = audioContextRef.current;
      const oscillator = ctx.createOscillator();
      const gainNode = ctx.createGain();

      oscillator.connect(gainNode);
      gainNode.connect(ctx.destination);

      oscillator.type = 'sine';
      oscillator.frequency.value = 1200; // Hz
      gainNode.gain.value = 0.2; // Slightly louder

      oscillator.start();
      setTimeout(() => {
        oscillator.stop();
      }, 300);
    } catch (e) {
      console.warn("Beep failed:", e);
    }
  };

  // Fetch orders
  useEffect(() => {
    // Scope strictly to this operator. Firestore throws if a where() value is
    // undefined, so wait until the operator is available.
    if (!operatorId) {
      setOrders([]);
      return undefined;
    }

    const fetchOrders = async () => {
      try {
        const q = query(
          collection(db, 'orderList'),
          where('operatorId', '==', operatorId),
          where('orderStatus', '==', 'paymentDeliveredToSeller'),
        );
        const querySnapshot = await getDocs(q);
        const ordersData = [];

        querySnapshot.forEach((doc) => {
          const data = doc.data();
          let orderTime = null;
          if (data.orderTime?.seconds) {
            orderTime = new Date(data.orderTime.seconds * 1000);
          } else if (data.addedAt) {
            orderTime = new Date(data.addedAt);
          }
          ordersData.push({ id: doc.id, ...data, orderTime });
        });

        ordersData.sort((a, b) => (a.orderTime || 0) - (b.orderTime || 0));
        setOrders(ordersData);
      } catch (error) {
        console.error("Fetch error:", error);
      }
    };
    fetchOrders();
  }, [operatorId]);

  // Continuous beep for overdue orders
  useEffect(() => {
    const checkAndBeep = () => {
      const now = new Date();
      const hasOverdue = orders.some(order => 
        order.orderTime && (now - order.orderTime) > 60000
      );

      if (hasOverdue) {
        // playBeep();
      }
    };

    // Start checking immediately and every 10s
    // checkAndBeep();
    // beepIntervalRef.current = setInterval(checkAndBeep, 10000);

    return () => {
      if (beepIntervalRef.current) clearInterval(beepIntervalRef.current);
    };
  }, [orders]);

  const formatDate = (date) => date ? date.toLocaleString('en-IN') : '—';
  const getAge = (orderTime) => {
    if (!orderTime) return '—';
    const diffMs = new Date() - orderTime;
    const mins = Math.floor(diffMs / 60000);
    const secs = Math.floor((diffMs % 60000) / 1000);
    return `${mins}m ${secs}s`;
  };

  // Status badge styles
  const getStatusStyle = (status) => ({
    display: 'inline-block',
    padding: '2px 6px',
    fontSize: '11px',
    fontWeight: 'bold',
    borderRadius: '4px',
    textTransform: 'capitalize',
    backgroundColor: 
      status === 'Requested' ? '#fef3c7' :
      status === 'Accepted' ? '#dcfce7' :
      status === 'Assorted' ? '#ede9fe' : '#e5e7eb',
    color: 
      status === 'Requested' ? '#92400e' :
      status === 'Accepted' ? '#166534' :
      status === 'Assorted' ? '#5b21b6' : '#374151'
  });

  return (
    <Layout pageTitle="Payments Paid">
      <div style={{ padding: '16px', overflowX: 'auto' }}>
        {orders.length === 0 ? (
          <p style={{
            textAlign: 'center',
            color: '#6b7280',
            paddingTop: '32px',
            fontSize: '18px'
          }}>
            No payments received.
          </p>
        ) : (
          <table style={{
            minWidth: '100%',
            borderCollapse: 'collapse',
            border: '2px solid #1f2937'
          }}>
            <thead>
              <tr style={{ backgroundColor: '#f3f4f6' }}>
                <th style={tableHeaderStyle}>Order Time</th>
                <th style={tableHeaderStyle}>Age</th>
                <th style={tableHeaderStyle}>Product</th>
                <th style={tableHeaderStyle}>Image</th>
                <th style={tableHeaderStyle}>Seller</th>
                <th style={tableHeaderStyle}>Net Wt (g)</th>
                <th style={tableHeaderStyle}>Gross Wt (g)</th>
                <th style={tableHeaderStyle}>Status</th>
                <th style={tableHeaderStyle}>Variants</th>
              </tr>
            </thead>
            <tbody>
              {orders.map((order) => {
                const isOverdue = order.orderTime && new Date() - order.orderTime > 60000;
                // 🔴 Highlight: bold red border around the entire row
                const rowBorder = isOverdue 
                  ? '3px solid #ef4444' 
                  : '2px solid #1f2937';

                return (
                  <tr 
                    key={order.id} 
                    style={{ 
                      backgroundColor: isOverdue ? '#fee2e2' : '#ffffff',
                      border: rowBorder,
                      outline: isOverdue ? '2px solid #fecaca' : 'none', // extra emphasis
                      outlineOffset: '-2px'
                    }}
                  >
                    <td style={tableCellStyle}>{formatDate(order.orderTime)}</td>
                    <td style={{ ...tableCellStyle, fontFamily: 'monospace' }}>{getAge(order.orderTime)}</td>
                    <td style={{ ...tableCellStyle, fontWeight: 'bold' }}>{order.productName || '—'}</td>
                    <td style={tableCellStyle}>
                      {order.images?.[0]?.url ? (
                        <img
                          src={order.images[0].url.trim()}
                          alt="Product"
                          style={{ height: '40px', width: '40px', objectFit: 'contain', border: '1px solid #d1d5db', borderRadius: '4px' }}
                        />
                      ) : (
                        <span style={{ color: '#9ca3af' }}>—</span>
                      )}
                    </td>
                    <td style={tableCellStyle}>{order.sellerId || '—'}</td>
                    <td style={tableCellStyle}>{order.netWt || '—'}</td>
                    <td style={tableCellStyle}>{order.grossWt || '—'}</td>
                    <td style={tableCellStyle}>
                      <span style={getStatusStyle(order.orderStatus)}>
                        {order.orderStatus}
                      </span>
                    </td>
                    <td style={tableCellStyle}>
                      {order.selectedVariants?.length ? (
                        order.selectedVariants.map((v, i) => (
                          <div key={i} style={{ whiteSpace: 'nowrap' }}>
                            {v.size} × {v.quantity}
                          </div>
                        ))
                      ) : (
                        <span>—</span>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>
    </Layout>
  );
};

// Reusable styles
const tableHeaderStyle = {
  border: '2px solid #1f2937',
  padding: '8px 12px',
  textAlign: 'left',
  fontSize: '13px',
  fontWeight: 'bold',
  backgroundColor: '#f3f4f6',
  color: '#4b5563',
};

const tableCellStyle = {
  border: '2px solid #1f2937',
  padding: '8px 12px',
  fontSize: '14px',
};

export default PaymentsPaid;